import api from "@/lib/api";
import type {
  ApiDilemma,
  ApiDilemmasImportFromExcelResponse,
  ApiUploadedFile,
  DilemmaCreationPayload,
  DilemmasListParams,
} from "../types";
import type { PaginatedQueryResult } from "@/types";

const DILEMMAS_ENDPOINTS = {
  LIST: "/dilemmas/",
  GET_ONE: (id: string) => `/dilemmas/${id}/`,
  CREATE: "/dilemmas/",
  UPDATE: (id: string) => `/dilemmas/${id}/`,
  DELETE: (id: string) => `/dilemmas/${id}/`,
  UPLOAD_FILE: "/uploaded-files/",
  DELETE_FILE: (id: string) => `/uploaded-files/${id}/`,
  BULK_UPDATE: "/dilemmas/bulk_update/",
  BULK_DESTROY: "/dilemmas/bulk_destroy/",
  CREATE_FROM_EXCEL: "/dilemmas/create-from-excel/",
};

export class DilemmasHttpRepository {
  async fetchList(
    params?: DilemmasListParams,
  ): Promise<PaginatedQueryResult<ApiDilemma>> {
    const result = await api.get(DILEMMAS_ENDPOINTS.LIST, { params });
    return result.data;
  }

  async fetchOne(id: string): Promise<ApiDilemma> {
    const result = await api.get(DILEMMAS_ENDPOINTS.GET_ONE(id));
    return result.data;
  }

  async create(payload: Partial<DilemmaCreationPayload>): Promise<ApiDilemma> {
    const result = await api.post(DILEMMAS_ENDPOINTS.CREATE, payload);
    return result.data;
  }

  async update(
    id: string,
    payload: Partial<DilemmaCreationPayload>,
  ): Promise<ApiDilemma> {
    const result = await api.put(DILEMMAS_ENDPOINTS.UPDATE(id), payload);
    return result.data;
  }

  async patch(
    id: string,
    payload: Partial<DilemmaCreationPayload>,
    params?: Record<string, unknown>,
  ): Promise<ApiDilemma> {
    const result = await api.patch(DILEMMAS_ENDPOINTS.UPDATE(id), payload, {
      params,
    });
    return result.data;
  }

  async delete(id: string) {
    const result = await api.delete(DILEMMAS_ENDPOINTS.DELETE(id));
    return result.data;
  }

  async bulkUpdate(payload: { id: string; status: string }[]) {
    const result = await api.patch(DILEMMAS_ENDPOINTS.BULK_UPDATE, payload);
    return result.data;
  }
  async bulkDestroy(payload: { id: string }[]) {
    const result = await api.delete(DILEMMAS_ENDPOINTS.BULK_DESTROY, {
      data: payload,
    });
    return result.data;
  }
  async deleteFile(id: string) {
    const result = await api.delete(DILEMMAS_ENDPOINTS.DELETE_FILE(id));
    return result.data;
  }

  async uploadFile(
    file: File,
    dilemmaId: string,
    tags: string[] = ["documentation"],
    onProgress?: (progress: number) => void,
  ): Promise<ApiUploadedFile> {
    const formData = new FormData();
    formData.append("uploaded_file", file);
    formData.append("dilemma", dilemmaId);
    tags.forEach((tag) => formData.append("tags", tag));

    const result = await api.post(DILEMMAS_ENDPOINTS.UPLOAD_FILE, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    });
    return result.data;
  }

  async importFromExcel(
    file: File,
    onProgress?: (progress: number) => void,
  ): Promise<ApiDilemmasImportFromExcelResponse> {
    const formData = new FormData();
    formData.append("file", file);

    const result = await api.post(
      DILEMMAS_ENDPOINTS.CREATE_FROM_EXCEL,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (onProgress && e.total) {
            onProgress(Math.round((e.loaded * 100) / e.total));
          }
        },
      },
    );

    return result.data;
  }
}
